import { useEffect, useState } from "react";
import {
  useGetRateLimitConfigQuery,
  useUpdateRateLimitConfigMutation,
} from "../../redux/api";
import { allRoles, roleLabels } from "../../config/roles-config";

type OverrideRow = {
  enabled: boolean;
  requests: string;
  windowSeconds: string;
};

const n = (v: string) => Number(v) || 0;

// Platform Owner → Settings → Rate Limiting. Edits the global config that the
// services rate-limit middleware reads. Server-side throttling only activates
// when this is enabled AND Upstash credentials are set on the server.
const RateLimit = () => {
  const { data: config, isLoading } = useGetRateLimitConfigQuery();
  const [updateConfig, { isLoading: saving }] =
    useUpdateRateLimitConfigMutation();

  const [enabled, setEnabled] = useState("no");
  const [requests, setRequests] = useState("100");
  const [windowSeconds, setWindowSeconds] = useState("60");
  const [overrides, setOverrides] = useState<Record<string, OverrideRow>>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!config) return;
    setEnabled(config.enabled ? "yes" : "no");
    setRequests(String(config.requests));
    setWindowSeconds(String(config.windowSeconds));

    const map: Record<string, OverrideRow> = {};
    for (const role of allRoles) {
      const existing = config.roleOverrides?.find((o) => o.role === role);
      map[role] = existing
        ? {
            enabled: true,
            requests: String(existing.requests),
            windowSeconds: String(existing.windowSeconds),
          }
        : {
            enabled: false,
            requests: String(config.requests),
            windowSeconds: String(config.windowSeconds),
          };
    }
    setOverrides(map);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.id, config?.updatedAt]);

  const setOverride = (role: string, patch: Partial<OverrideRow>) =>
    setOverrides((prev) => ({ ...prev, [role]: { ...prev[role], ...patch } }));

  const handleSave = async () => {
    setError(null);
    setSaved(false);
    try {
      const roleOverrides = allRoles
        .filter((r) => overrides[r]?.enabled)
        .map((r) => ({
          role: r,
          requests: n(overrides[r].requests),
          windowSeconds: n(overrides[r].windowSeconds),
        }));

      await updateConfig({
        enabled: enabled === "yes",
        requests: n(requests),
        windowSeconds: n(windowSeconds),
        roleOverrides,
      }).unwrap();
      setSaved(true);
    } catch (err) {
      setError(
        (err as { message?: string })?.message ??
          "Failed to save rate-limit config.",
      );
    }
  };

  return (
    <div className="dashboard">
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Rate Limiting</h2>
          </div>
        </div>
        <div className="panel__body">
        {error ? <div className="form-error">{error}</div> : null}
        {saved ? (
          <div
            style={{
              margin: "0 0 0.9rem",
              padding: "0.55rem 0.75rem",
              borderRadius: "var(--radius-sm)",
              background: "var(--success-soft)",
              color: "var(--success)",
              fontWeight: 500,
            }}
          >
            Rate-limit settings saved.
          </div>
        ) : null}

        {isLoading ? (
          <p>Loading…</p>
        ) : (
          <>
            <div className="form-grid">
              <label className="field">
                Status
                <select
                  value={enabled}
                  onChange={(e) => setEnabled(e.target.value)}
                >
                  <option value="no">Disabled</option>
                  <option value="yes">Enabled</option>
                </select>
              </label>
              <label className="field">
                Default requests
                <input
                  type="number"
                  min={1}
                  value={requests}
                  onChange={(e) => setRequests(e.target.value)}
                />
              </label>
              <label className="field">
                Window (seconds)
                <input
                  type="number"
                  min={1}
                  value={windowSeconds}
                  onChange={(e) => setWindowSeconds(e.target.value)}
                />
              </label>
            </div>

            <h2 style={{ margin: "1.5rem 0 0.3rem", fontSize: "1rem" }}>
              Per-role overrides
            </h2>
            <p
              style={{
                margin: "0 0 0.9rem",
                color: "var(--text-muted)",
                fontSize: "0.85rem",
              }}
            >
              Tick a role to give it a different limit. Unticked roles use the
              default above.
            </p>

            <div style={{ display: "grid", gap: "0.5rem" }}>
              {allRoles.map((role) => {
                const row =
                  overrides[role] ?? {
                    enabled: false,
                    requests,
                    windowSeconds,
                  };
                return (
                  <div
                    key={role}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.6rem",
                      flexWrap: "wrap",
                    }}
                  >
                    <label
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.4rem",
                        minWidth: "10rem",
                        fontWeight: 500,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={row.enabled}
                        onChange={(e) =>
                          setOverride(role, { enabled: e.target.checked })
                        }
                      />
                      {roleLabels[role] ?? role}
                    </label>
                    <input
                      type="number"
                      min={1}
                      style={{ width: "7rem" }}
                      disabled={!row.enabled}
                      value={row.requests}
                      onChange={(e) =>
                        setOverride(role, { requests: e.target.value })
                      }
                    />
                    <span style={{ color: "var(--text-muted)" }}>requests /</span>
                    <input
                      type="number"
                      min={1}
                      style={{ width: "6rem" }}
                      disabled={!row.enabled}
                      value={row.windowSeconds}
                      onChange={(e) =>
                        setOverride(role, { windowSeconds: e.target.value })
                      }
                    />
                    <span style={{ color: "var(--text-muted)" }}>seconds</span>
                  </div>
                );
              })}
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn--primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </>
        )}
        </div>
      </section>
    </div>
  );
};

export default RateLimit;
